import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [guestReady, setGuestReady] = useState(false);
  const [unread, setUnread] = useState(0);
  const [wsReady, setWsReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const threadIdRef = useRef(null); // ref so connectWs always has current value

  const isLoggedIn = !!user && !!token;
  const needsEmail = !isLoggedIn && !guestReady;

  // Unread count for logged-in users
  useEffect(() => {
    if (!isLoggedIn) return;
    const check = async () => {
      try { const r = await api.get('/chat/unread-count'); setUnread(r.data.count); } catch {}
    };
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, [isLoggedIn]);

  // Auto-init thread for logged-in users when chat opens
  useEffect(() => {
    if (!open || !isLoggedIn || !user?.customer_id) return;
    if (threadIdRef.current) {
      // Reconnect WS if needed
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        connectWs(threadIdRef.current);
      }
      return;
    }
    initLoggedInThread();
  }, [open, isLoggedIn]);

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initLoggedInThread = async () => {
    try {
      const t = await api.post('/chat/threads', { customer_id: user.customer_id });
      const msgs = await api.get(`/chat/threads/${t.data.id}/messages`);
      setThreadId(t.data.id);
      threadIdRef.current = t.data.id;
      setMessages(msgs.data);
      connectWs(t.data.id);
      setUnread(0);
    } catch (e) {
      console.error('Chat init failed:', e);
    }
  };

  const connectWs = (tid) => {
    if (wsRef.current) wsRef.current.close();
    const wsBase = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsUrl = isLoggedIn
      ? `${wsBase}/api/chat/ws/${tid}?token=${token}`
      : `${wsBase}/api/chat/ws/${tid}?guest_email=${encodeURIComponent(email)}&guest_name=${encodeURIComponent(name || email)}`;

    const ws = new WebSocket(wsUrl);
    ws.onopen = () => setWsReady(true);
    ws.onclose = () => setWsReady(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages(prev => [...prev, msg]);
      } catch {}
    };
    wsRef.current = ws;
  };

  // ── FIX: Don't rely on state in async closure — use values directly ──────
  const handleGuestStart = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    setStarting(true);

    try {
      // Use email/name directly from current scope — no state dependency
      const t = await api.post('/chat/threads', {
        guest_email: trimmedEmail,
        guest_name: name.trim() || trimmedEmail,
      });
      const msgs = await api.get(`/chat/threads/${t.data.id}/messages`);

      // Update all state together AFTER we have the data
      setThreadId(t.data.id);
      threadIdRef.current = t.data.id;
      setMessages(msgs.data);
      setGuestReady(true); // now mark ready AFTER thread is created

      // connectWs uses wsRef closure — pass email directly
      connectWsGuest(t.data.id, trimmedEmail, name.trim() || trimmedEmail);
    } catch (err) {
      console.error('Chat start failed:', err);
    } finally {
      setStarting(false);
    }
  };

  // Separate connect function for guest that uses values directly (not state)
  const connectWsGuest = (tid, guestEmail, guestName) => {
    if (wsRef.current) wsRef.current.close();
    const wsBase = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsUrl = `${wsBase}/api/chat/ws/${tid}?guest_email=${encodeURIComponent(guestEmail)}&guest_name=${encodeURIComponent(guestName)}`;

    const ws = new WebSocket(wsUrl);
    ws.onopen = () => setWsReady(true);
    ws.onclose = () => setWsReady(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages(prev => [...prev, msg]);
      } catch {}
    };
    wsRef.current = ws;
  };

  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ content: input.trim() }));
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const myId = isLoggedIn ? user.id : `guest_${email}`;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="chat-widget-btn"
        className="fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-[#3B82F6] text-white shadow-[0_0_25px_rgba(59,130,246,0.4)] hover:bg-[#2563EB] transition-all flex items-center justify-center"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center font-['JetBrains_Mono']">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-36 right-6 z-50 w-80 sm:w-96 rounded-sm shadow-2xl border border-[#2D3748] flex flex-col overflow-hidden"
          style={{ height: 480 }}
        >
          {/* Header */}
          <div className="bg-[#1A2028] border-b border-[#2D3748] px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-[#3B82F6]/15 border border-[#3B82F6]/30 flex items-center justify-center flex-shrink-0">
              <span className="text-[#3B82F6] text-xs font-bold font-['Manrope']">MM</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-xs font-bold font-['Manrope']">Millennials Maintenance</p>
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${wsReady ? 'bg-[#22C55E]' : 'bg-[#7B8EA2]'}`} />
                <span className={`text-[10px] font-['JetBrains_Mono'] ${wsReady ? 'text-[#22C55E]' : 'text-[#7B8EA2]'}`}>
                  {wsReady ? 'Connected' : 'Team Online'}
                </span>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#7B8EA2] hover:text-white transition-colors p-1">
              <X size={16} />
            </button>
          </div>

          {/* Email prompt for guests */}
          {needsEmail ? (
            <div className="flex-1 bg-[#0F1419] flex flex-col items-center justify-center p-6">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#7B8EA2] mb-2 text-center">// START CONVERSATION</p>
              <p className="text-white font-['Manrope'] font-bold text-lg mb-1 text-center">Talk to our team</p>
              <p className="text-[#C8D4E0] font-['IBM_Plex_Sans'] text-sm mb-6 text-center">We usually reply within minutes</p>
              <form onSubmit={handleGuestStart} className="w-full space-y-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full h-10 px-3 bg-[#1A2028] border border-[#2D3748] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
                />
                <input
                  type="email"
                  placeholder="Email address *"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full h-10 px-3 bg-[#1A2028] border border-[#2D3748] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
                  data-testid="chat-email-input"
                />
                <button
                  type="submit"
                  disabled={starting || !email.trim()}
                  className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="chat-start-btn"
                >
                  {starting && <Loader2 size={14} className="animate-spin" />}
                  {starting ? 'Connecting...' : 'Start Chat'}
                </button>
              </form>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 bg-[#0F1419] overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-[#7B8EA2] font-['IBM_Plex_Sans'] text-sm">
                      Hi {isLoggedIn ? user.full_name?.split(' ')[0] : (name || 'there')}! How can we help?
                    </p>
                  </div>
                )}
                {messages.map(m => {
                  const isMine = isLoggedIn
                    ? m.sender_id === user.id
                    : m.sender_id === `guest_${email}`;
                  return (
                    <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm font-['IBM_Plex_Sans'] ${isMine ? 'bg-[#3B82F6] text-white' : 'bg-[#242D38] text-white border border-[#2D3748]'}`}>
                        {!isMine && (
                          <p className="text-[10px] font-bold mb-1 font-['Manrope'] text-[#3B82F6]">{m.sender_name}</p>
                        )}
                        <p>{m.content}</p>
                        <p className={`text-[10px] mt-1 ${isMine ? 'text-white/50' : 'text-[#7B8EA2]'}`}>
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="bg-[#1A2028] border-t border-[#2D3748] p-3 flex items-end gap-2 flex-shrink-0">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder={wsReady ? 'Type a message...' : 'Connecting...'}
                  disabled={!wsReady}
                  data-testid="chat-input"
                  className="flex-1 px-3 py-2 bg-[#0A0D10] border border-[#2D3748] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors resize-none disabled:opacity-50"
                  style={{ maxHeight: 80 }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || !wsReady}
                  data-testid="chat-send-btn"
                  className="w-9 h-9 bg-[#3B82F6] text-white rounded-sm hover:bg-[#2563EB] disabled:opacity-40 transition-colors flex items-center justify-center flex-shrink-0"
                >
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
